/**
 * Host controls for which rounds are open to phone answers.
 *
 * useRoundLocks() loads every round's Open or Closed state and keeps it
 * live on the "locks-changed" signal. Changes go through the authenticated
 * REST route only (POST /api/answers/event/:id/round/:n/lock), never
 * through a socket message.
 *
 * <RoundLocks> lists every round with one Open or Close button.
 * <CurrentRoundLockButton> is the big one-click toggle for the round the
 * host is on, for the host panel.
 */
import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Flex, Text } from '@radix-ui/themes';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useRoomSignals } from '../../utils/useRealtime';
import './RoundLocks.css';

export interface RoundState {
  roundNumber: number;
  title: string;
  roundType: 'STANDARD' | 'VISUAL' | 'AUDIO';
  locked: boolean;
  openedByHost: boolean;
}

export interface RoundLocksState {
  rounds: RoundState[] | null;
  mode: 'ON_REVEAL' | 'MANUAL';
  answerMode: 'PAPER' | 'PHONE' | 'MIXED';
  busy: number | null;
  setLocked: (roundNumber: number, locked: boolean) => Promise<void>;
}

export function useRoundLocks(eventId: string, onChange?: (lockedRounds: number[]) => void): RoundLocksState {
  const [rounds, setRounds] = useState<RoundState[] | null>(null);
  const [mode, setMode] = useState<'ON_REVEAL' | 'MANUAL'>('ON_REVEAL');
  const [answerMode, setAnswerMode] = useState<'PAPER' | 'PHONE' | 'MIXED'>('PAPER');
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/answers/event/${eventId}/rounds`);
      const data = res.data.data;
      setRounds(data.rounds);
      setMode(data.roundCloseMode);
      setAnswerMode(data.answerMode);
    } catch {
      /* the next signal or reload retries */
    }
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = useCallback(
    (lockedRounds: number[], openRounds: number[]) => {
      setRounds((rs) =>
        rs
          ? rs.map((r) => ({
              ...r,
              locked: lockedRounds.includes(r.roundNumber),
              openedByHost: openRounds.includes(r.roundNumber),
            }))
          : rs,
      );
      onChange?.(lockedRounds);
    },
    [onChange],
  );

  useRoomSignals(
    { event: 'join-host', eventId },
    {
      'locks-changed': (p) => {
        const { lockedRounds, openRounds } = (p ?? {}) as { lockedRounds?: number[]; openRounds?: number[] };
        if (lockedRounds) apply(lockedRounds, openRounds ?? []);
      },
      // The answer mode or the close setting changed in the hub.
      'teams-changed': load,
    },
    load,
  );

  const setLocked = useCallback(
    async (roundNumber: number, locked: boolean) => {
      setBusy(roundNumber);
      try {
        const res = await api.post(`/api/answers/event/${eventId}/round/${roundNumber}/lock`, { locked });
        const { lockedRounds, openRounds } = res.data.data as { lockedRounds: number[]; openRounds: number[] };
        apply(lockedRounds, openRounds);
      } catch {
        toast.error(locked ? 'Could not close the answers' : 'Could not reopen the answers');
      } finally {
        setBusy(null);
      }
    },
    [eventId, apply],
  );

  return { rounds, mode, answerMode, busy, setLocked };
}

/** Every round with its state and one Open or Close button. */
export function RoundLocksView({ state, current, compact }: { state: RoundLocksState; current?: number | null; compact?: boolean }) {
  const { rounds, mode, answerMode, busy, setLocked } = state;
  if (!rounds || answerMode === 'PAPER') return null;
  return (
    <div className="rl-panel">
      <Flex justify="between" align="baseline" gap="2" wrap="wrap" mb="2">
        <Text size="2" weight="bold">Phone answers by round</Text>
        <Text size="1" color="gray">
          {mode === 'ON_REVEAL'
            ? 'A round closes when you reveal its answers. You can open or close any round.'
            : 'Rounds close only when you close them.'}
        </Text>
      </Flex>
      <div className={`rl-grid ${compact ? 'rl-compact' : ''}`}>
        {rounds.map((r) => (
          <div key={r.roundNumber} className={`rl-round ${r.roundNumber === current ? 'rl-current' : ''}`}>
            <Flex direction="column" gap="1" className="rl-label">
              <Text size="2" weight="bold">
                R{r.roundNumber}
                {r.roundType === 'VISUAL' ? ' Visual' : r.roundType === 'AUDIO' ? ' Audio' : ''}
              </Text>
              <Badge color={r.locked ? 'gray' : 'green'} size="1">
                {r.locked ? 'Closed' : r.openedByHost ? 'Open (kept open)' : 'Open'}
              </Badge>
            </Flex>
            <Button
              size="1"
              variant="soft"
              color={r.locked ? 'green' : 'red'}
              disabled={busy === r.roundNumber}
              onClick={() => setLocked(r.roundNumber, !r.locked)}
              aria-label={`${r.locked ? 'Reopen' : 'Close'} answers for round ${r.roundNumber}`}
            >
              {r.locked ? 'Reopen' : 'Close'}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The big toggle for the round the host is on. Shows the state at a glance
 * (a coloured OPEN or CLOSED tag) and flips it in one click.
 */
export function CurrentRoundLockButton({ state, roundNumber }: { state: RoundLocksState; roundNumber: number | null }) {
  const { rounds, answerMode, busy, setLocked } = state;
  if (!rounds || answerMode === 'PAPER' || roundNumber == null) return null;
  const r = rounds.find((x) => x.roundNumber === roundNumber);
  if (!r) return null;
  return (
    <button
      type="button"
      className={`rl-current-toggle ${r.locked ? 'is-closed' : 'is-open'}`}
      disabled={busy === r.roundNumber}
      onClick={() => setLocked(r.roundNumber, !r.locked)}
      aria-pressed={r.locked}
      title={r.locked ? 'Phones cannot send answers for this round. Click to reopen.' : 'Phones can send answers for this round. Click to close.'}
    >
      <span className="rl-current-state">
        R{r.roundNumber} phone answers: <b>{r.locked ? 'CLOSED' : 'OPEN'}</b>
      </span>
      <span className="rl-current-action">{r.locked ? 'Reopen answers' : 'Close answers'}</span>
    </button>
  );
}

/** Stand-alone panel: loads its own state. */
export default function RoundLocks({
  eventId,
  current,
  onChange,
  compact,
}: {
  eventId: string;
  current?: number | null;
  onChange?: (lockedRounds: number[]) => void;
  compact?: boolean;
}) {
  const state = useRoundLocks(eventId, onChange);
  return <RoundLocksView state={state} current={current} compact={compact} />;
}
